import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClientSocket } from '../websocket/ClientSocket';
import { ChessBoard } from './ChessBoard';
import { JesterDice } from './JesterDice';
import { GameStatus } from './GameStatus';
import { Footer } from './Footer';
import { AdBanner } from './AdBanner';
import type { GameState, Color } from '@jester-chess/shared';
import './GamePage.css';

export function GamePage() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [yourColor, setYourColor] = useState<Color | null>(null);
  const [waiting, setWaiting] = useState(true);
  const [socket, setSocket] = useState<ClientSocket | null>(null);
  const [gameUrl, setGameUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [playAgainLoading, setPlayAgainLoading] = useState(false);
  const [showResignConfirm, setShowResignConfirm] = useState(false);

  useEffect(() => {
    if (!gameId) return;

    // Reset states when gameId changes (e.g., after clicking "Play Again")
    setPlayAgainLoading(false);
    setWaiting(true);
    setGameState(null);
    setYourColor(null);

    // Set the game URL for sharing
    const url = `${window.location.origin}/game/${gameId}`;
    setGameUrl(url);

    const clientSocket = new ClientSocket();
    setSocket(clientSocket);

    clientSocket.onGameStart((state, color) => {
      setGameState(state);
      setYourColor(color);
      setWaiting(false);
    });

    clientSocket.onWaiting(() => {
      setWaiting(true);
    });

    clientSocket.onGameStateUpdate((state) => {
      setGameState(state);
    });

    clientSocket.onReconnect((state, color) => {
      setGameState(state);
      setYourColor(color);
      setWaiting(false);
    });

    clientSocket.onGameOver((winner, state) => {
      setGameState(state);
    });

    // Connect with gameId (stored for later use)
    clientSocket.setGameId(gameId);
    clientSocket.connect();

    return () => {
      clientSocket.disconnect();
    };
  }, [gameId]);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(gameUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handlePlayAgainBot = async () => {
    setPlayAgainLoading(true);
    try {
      const response = await fetch('/api/games/bot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to create bot game');
      const data = await response.json();
      navigate(`/game/${data.gameId}`);
    } catch (err) {
      console.error('Failed to create new bot game:', err);
      setPlayAgainLoading(false);
    }
  };

  const handlePlayAgainHuman = async () => {
    setPlayAgainLoading(true);
    try {
      const response = await fetch('/api/games', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to create game');
      const data = await response.json();
      navigate(`/game/${data.gameId}`);
    } catch (err) {
      console.error('Failed to create new game:', err);
      setPlayAgainLoading(false);
    }
  };

  if (waiting) {
    return (
      <div className="app waiting-app">
        <div className="waiting-screen">
          <div className="waiting-modal">
            <div className="waiting-icon">⏳</div>
            <h2 className="waiting-title">Waiting for Opponent</h2>
            <p className="waiting-subtitle">Share this link to invite a friend to play!</p>
            
            <div className="share-section">
              <div className="url-container">
                <input 
                  type="text" 
                  value={gameUrl} 
                  readOnly 
                  className="url-input"
                />
                <button onClick={handleCopyUrl} className="copy-button">
                  {copied ? '✓ Copied!' : '📋 Copy'}
                </button>
              </div>
            </div>

            <div className="waiting-divider">
              <span>or</span>
            </div>

            <div className="waiting-actions">
              <button 
                className="waiting-action-btn bot-btn"
                onClick={handlePlayAgainBot}
              >
                🤖 Play vs Bot Instead
              </button>
              <button 
                className="waiting-action-btn home-btn"
                onClick={() => navigate('/')}
              >
                🏠 Back to Home
              </button>
            </div>

            <div className="waiting-hint">
              <span className="pulse-dot"></span>
              Listening for opponent...
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!gameState || !yourColor) {
    return (
      <div className="app">
        <div className="loading">Loading game...</div>
      </div>
    );
  }

  const isGameOver = gameState.status === 'finished' || gameState.winner || gameState.isDraw;
  const isDraw = gameState.isDraw;
  const didYouWin = !isDraw && gameState.winner === yourColor;

  const handleResign = () => {
    setShowResignConfirm(true);
  };

  const confirmResign = () => {
    socket?.sendResign();
    setShowResignConfirm(false);
  };

  const cancelResign = () => {
    setShowResignConfirm(false);
  };

  return (
    <div className="app">
      <header>
        <h1>Jester Chess</h1>
        <GameStatus gameState={gameState} yourColor={yourColor} />
      </header>
      <main className="game-container">
        <div className="game-main">
          <ChessBoard gameState={gameState} yourColor={yourColor} socket={socket} />
          <div className="game-sidebar">
            <JesterDice
              gameState={gameState}
              yourColor={yourColor}
              socket={socket}
            />
            {/* Resign button - only show during active game */}
            {gameState.status === 'active' && (
              <button className="resign-button" onClick={handleResign}>
                🏳️ Resign
              </button>
            )}
          </div>
        </div>
      </main>

      {/* Resign Confirmation Modal */}
      {showResignConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-modal">
            <div className="confirm-icon">🏳️</div>
            <h2 className="confirm-title">Resign Game?</h2>
            <p className="confirm-message">
              Are you sure you want to surrender? Your opponent will be declared the winner.
            </p>
            <div className="confirm-buttons">
              <button className="confirm-btn confirm-yes" onClick={confirmResign}>
                Yes, I Resign
              </button>
              <button className="confirm-btn confirm-no" onClick={cancelResign}>
                Keep Playing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Game Over Overlay */}
      {isGameOver && (
        <div className="game-over-overlay">
          <div className="game-over-modal">
            <h2 className={isDraw ? 'draw-text' : didYouWin ? 'win-text' : 'lose-text'}>
              {isDraw ? '🤝 Draw!' : didYouWin ? '🎉 Victory!' : '💀 Defeat!'}
            </h2>
            <p className="game-over-message">
              {isDraw
                ? 'Stalemate - No legal moves available!'
                : gameState.winner 
                  ? `${gameState.winner.charAt(0).toUpperCase() + gameState.winner.slice(1)} wins!`
                  : 'Game Over'}
            </p>
            <div className="play-again-buttons">
              <button 
                className="play-again-bot"
                onClick={handlePlayAgainBot}
                disabled={playAgainLoading}
              >
                {playAgainLoading ? 'Starting...' : '🤖 Play Again vs Bot'}
              </button>
              <button 
                className="play-again-human"
                onClick={handlePlayAgainHuman}
                disabled={playAgainLoading}
              >
                {playAgainLoading ? 'Starting...' : '👥 Challenge a Friend'}
              </button>
              <button 
                className="back-home"
                onClick={() => navigate('/')}
                disabled={playAgainLoading}
              >
                🏠 Back to Home
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Single ad shown only during active gameplay - meaningful publisher content */}
      <div className="ad-footer">
        <AdBanner 
          adUnitId={import.meta.env.VITE_ADSENSE_CLIENT_ID}
          adSlot={import.meta.env.VITE_ADSENSE_SLOT_BANNER}
          format="banner"
        />
      </div>
      
      <Footer />
    </div>
  );
}

